<template>
    <div>
	<div class="analytics-table">
        <Loader :show="isLoading"/>

            <TemplateModal
                ref="cgTemplateModal"
                :company-data="modalCompanyData"
                :campaign-id="campaignId"
                :is-read-only="true"
                :trigger="selTemplateRef"
            ></TemplateModal>
            <Txt weight="bold" class="analytics-card-title mb-3" no-margin>{{$t('Dashboard.Charts.ClickRateByParam', {param : 'template'})}}</Txt>
            <CGTable v-if="itemsShown.length" v-model:sort-key="sortKey" v-model:sort-direction="sortDir" :name="$t('Navbar.Template')" alternate-color="white">
                <template #head> 
                    <TableCell v-for="prop in props" :key="prop.key" :sort-key="prop.key" head>{{ prop.title }}</TableCell>
                    <TableCell head>{{ $t('Template.Preview') }}</TableCell>
                </template>
                <TableRow v-for="(template, i) in itemsShown" :key="i" :record="template">
                    <TableCell>
                        <Txt weight="bold">{{ template.name }}</Txt>
                    </TableCell>
                    <TableCell>{{  $t(template.used_last? 'General.Yes' : 'General.No') }}</TableCell>
                    <TableCell>{{ $filters.templateType(template.type)  }}</TableCell>
                    <TableCell>{{ template.sent }}</TableCell>
                    <TableCell>{{ template.clicked }}</TableCell>
                    <TableCell center>{{ template.click_rate }}{{ template.sent ? '%' : '' }}</TableCell>
                    <TableCell>
                        <CGButton :ref="'template_' + template.template_id" small variant="ghost" :aria-label="$t('General.Show')" class="text-primary-300 hover:text-violet-700"
                            @click="preview(template)" @keydown.enter="preview(template)">
                            <Icon :size="18" name="eye"/>
                        </CGButton>
                    </TableCell>
                </TableRow>
            </CGTable>
            <div v-else class="my-8">
				<Txt weight="medium">{{ $t('General.TableDataPending') }}</Txt>
			</div>
        </div>
        <Pagination v-if="data.length" :page="page" :items-per-page="itemsPerPage" :total-items="data.length" :context="$t('General.TemplatesPerPage')" class="mt-2" @page-change="pageChange"  />
    </div>
</template> 

<script>
import { defineComponent } from 'vue';

import campaignService from '@/services/campaign.service.js'
import phishingService from '@/services/phishing.service.js'

export default defineComponent({

  props: {
      apiFilter: Object,
      companyData: Object
  },

  data() {
      return {
          isLoading: false,
          data: [],
          page: 1,
          itemsShown : [],
          itemsPerPage : 10,
          title: this.$t('Dashboard.Charts.ClickRateByParam', { param : 'template' }),
          props:[ 
              { title: this.$t('Dashboard.Charts.TemplateName'), key: 'name' },
              { title: this.$t('Dashboard.Charts.Active'), key: 'used_last' },
              { title: this.$t('Dashboard.Charts.Type'), key: 'type' },
              { title: this.$t('Dashboard.Charts.Sent'), key: 'sent' },
              { title: this.$t('Dashboard.Charts.Clicked'), key: 'clicked' },
              { title: this.$t('Dashboard.Charts.ClickRate'), key: 'click_rate' }
          ],
          campaignId: null,
          config: null,
          counter: 0,
          selTemplateRef: null,
          sortKey: 'click_rate',
          sortDir: 'desc'
      }
	},

  computed: {
      modalCompanyData() {
          return {
              company: this.companyData,
              company_id: this.companyData?.company_id,
              img_url: this.config?.defaults?.campaign?.img_url || '{{.BaseURL}}',
              gp_landing_url: this.config?.defaults?.gp_landing_url,
          };
      },
  },

  watch: {
      '$props.apiFilter': {
          handler() {
              this.loadData()
          },
          deep: true
      },
    sortKey(newVal, oldVal) {
        if(oldVal !== newVal) {
            this.page = 1; // Reset to first page on sort change
        }
        this.sortData();
    },
    sortDir() {
        this.sortData();
    },
  },

  async created() {
      this.config = (await phishingService.getConfig()).data;
  },

  mounted() {
      this.loadData()
  },

  methods:{
      async loadData() {
          try {
              this.counter++;
              this.isLoading = true;
              const res = await phishingService.getCompanySummary(this.companyData.company_id, { code: 'template', ...this.apiFilter });    

              this.data = res.data.map(item => ({
                  ...item,
                  click_rate: item.sent? ((item.clicked / item.sent) * 100).toFixed(2) : '-'
              }));

              this.sortData();
          } catch (error) {
              this.$eventBus.$emit('show-alert', {title: this.$t('General.DataFetchError'), variant: 'danger'});
          } finally {
              if (this.counter > 0) this.counter--;

              if (!this.counter) this.isLoading = false;
          }
      },
      sortData() {
        if (!this.data || !this.sortKey) return;

        const dir = this.sortDir === 'desc' ? -1 : 1;

        this.data.sort((a, b) => {
            let valA = a[this.sortKey];
            let valB = b[this.sortKey];

            // Handle undefined/null values
            if (valA == null) valA = '';
            if (valB == null) valB = '';

            // Numeric sort if possible, otherwise string
            if (!isNaN(valA) && !isNaN(valB)) {
                return (valA - valB) * dir;
            }
            return valA.toString().localeCompare(valB.toString()) * dir;
        });

        this.showItems();
    },
      pageChange(page, itemsPerPage) {
          this.page = page
          this.itemsPerPage = itemsPerPage
          this.showItems()
      },
      showItems() {
          const start = (this.itemsPerPage*this.page)-this.itemsPerPage
          const end = this.itemsPerPage*this.page
          this.itemsShown = [...this.data].slice(start, end)
      },
      async preview(item) {
          try {
              this.$eventBus.$emit('loading', true);
              let result = await campaignService.getCampaignTemplateById(this.companyData.company_id, item.campaign_id, item.template_id);
              item.body = result.data.length ? result.data : [{ body: "", hints: 0 }];
              item.languages = result.data.length;
              item.landing_page_url = this.config.defaults.gp_landing_url;
              this.template = item;
              this.campaignId = item.campaign_id;
              this.selTemplateRef = this.$refs['template_' + item.template_id]; 
              this.$refs.cgTemplateModal.showPreviewModal(this.template);
          } catch (error) {
              this.$eventBus.$emit('show-alert', { title: this.$t('Template.TemplateNotFoundError'), variant: 'danger' });
          } finally {
              this.$eventBus.$emit('loading', false);
          }
      },
  },
});
</script>


<style scoped lang="scss">
    .analytics-table {
        padding: 20px;
        background-color: white;
        /* border: solid 1px #050d2c; */
        border: solid 1px rgb(229, 231, 235);
        max-height: 650px;
        min-height: 150px;
        overflow: auto;
        position: relative;

        .white-overlay {
            position: absolute;
            top: 0;
            left: 0;
        }
    }
</style>